"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import {
  MapPin,
  Mail,
  Phone,
  User,
  ArrowLeft,
  CheckCircle2,
  Layers,
  Shield,
  ChevronRight,
  Building2,
  Loader2,
  Lock,
  X,
} from "lucide-react";
import { useAuth } from "@/modules/auth/context/AuthContext";
import { useProductDetailPage } from "@/modules/public/home/hooks/usePublicData";

interface ProductDetailClientProps {
  slug: string;
  categorySlug: string;
  subcatSlug: string;
}

export default function ProductDetailClient({
  slug,
  categorySlug,
  subcatSlug,
}: ProductDetailClientProps) {
  const router = useRouter();
  const { user } = useAuth();

  const {
    product,
    loading,
    error,
    buyerName,
    setBuyerName,
    buyerPhone,
    setBuyerPhone,
    buyerEmail,
    setBuyerEmail,
    buyerLocation,
    setBuyerLocation,
    message,
    setMessage,
    submitting,
    submitSuccess,
    setSubmitSuccess,
    submitError,
    selectedVariantIndex,
    setSelectedVariantIndex,
    isEnquiryModalOpen,
    setIsEnquiryModalOpen,
    handleEnquirySubmit,
  } = useProductDetailPage(slug);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="w-12 h-12 text-[#0f9648] animate-spin mb-4" />
        <p className="text-gray-605 font-medium">Fetching premium product details...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center max-w-md">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">{error || "Product could not be found."}</p>
          <button
            onClick={() => router.back()}
            className="inline-flex items-center gap-2 bg-green-600 text-white font-semibold px-6 py-2.5 rounded-md hover:bg-green-700 transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Catalog
          </button>
        </div>
      </div>
    );
  }

  const currentVariant =
    product.variants && product.variants.length > selectedVariantIndex
      ? product.variants[selectedVariantIndex]
      : null;

  const discountPercentage =
    currentVariant && currentVariant.mrpPrice > 0
      ? Math.round(((currentVariant.mrpPrice - currentVariant.salePrice) / currentVariant.mrpPrice) * 100)
      : 0;

  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      {/* Breadcrumbs Banner */}
      <div className="bg-white border-b border-gray-200 py-3 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 overflow-x-auto whitespace-nowrap">
            <Link href="/" className="hover:text-green-600 transition-colors font-medium">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
            <Link href={`/${categorySlug}`} className="hover:text-green-600 transition-colors font-medium">
              {product.category?.name || "Category"}
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
            <Link
              href={`/${categorySlug}/${subcatSlug}`}
              className="hover:text-green-600 transition-colors font-medium"
            >
              {product.subcategory?.name || "Subcategory"}
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
            <span className="font-bold text-green-700 truncate max-w-xs">{product.title}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <button
          onClick={() => router.back()}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-green-600 font-bold mb-6 group transition"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back
        </button>

        {/* Top Product details (grid) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
          {/* Left Column: Image */}
          <div className="md:col-span-5 flex flex-col w-full h-full">
            <div className="relative flex-1 rounded-sm border border-gray-200 bg-gray-200 flex items-center justify-center p-6 min-h-[300px] md:min-h-full overflow-hidden group">
              {product.imageUrl ? (
                <Image
                  src={product.imageUrl}
                  alt={product.title}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 40vw, 33vw"
                  priority
                  className="object-contain p-6 hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <Layers className="w-20 h-20 text-gray-300" />
              )}
              <div className="absolute top-4 left-4 bg-green-600 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded shadow-md flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" /> Verified Vendor
              </div>
            </div>
          </div>

          {/* Right Column: Info & Description */}
          <div className="md:col-span-7 flex flex-col justify-between space-y-4">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">
                  {product.title}
                </h1>
                {product.tagline && (
                  <p className="text-sm sm:text-base font-bold text-emerald-700 leading-normal">
                    {product.tagline}
                  </p>
                )}
              </div>

              {/* Price block */}
              {user ? (
                <div className="space-y-4">
                  {currentVariant ? (
                    <div className="space-y-1">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="text-3xl font-black text-gray-900">₹{currentVariant.salePrice}</span>
                        {currentVariant.mrpPrice > currentVariant.salePrice && (
                          <>
                            <span className="text-green-800 line-through text-lg">₹{currentVariant.mrpPrice}</span>
                            <span className="bg-[#e8f5e9] text-[#0f9648] text-xs font-black px-2.5 py-1 rounded">
                              {discountPercentage}% OFF
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-gray-700 font-bold">Inclusive of all taxes</p>
                    </div>
                  ) : (
                    <div className="text-sm font-bold text-gray-500 italic">Contact Vendor for Pricing</div>
                  )}

                  <p className="text-xs text-gray-500 font-bold">
                    Subcategory: <span className="text-gray-700 font-black">{product.subcategory?.name}</span> |
                    Type: <span className="text-gray-700 font-black">{product.category?.name}</span>
                  </p>

                  {/* Stock Status & Variant Buttons */}
                  <div className="flex items-center gap-3 flex-wrap pt-1">
                    {product.inStock !== false ? (
                      <span className="inline-flex items-center justify-center h-6 bg-[#e8f5e9] text-[#0f9648] border border-[#a5d6a7] text-[11px] font-black uppercase tracking-wide px-3 rounded">
                        In Stock
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center h-8 bg-red-50 text-red-700 border border-red-200 text-[11px] font-black uppercase tracking-wide px-3 rounded">
                        Out of Stock
                      </span>
                    )}

                    {product.variants && product.variants.length > 0 && (
                      <div className="flex gap-2 flex-wrap items-center">
                        {product.variants.map((v, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedVariantIndex(idx)}
                            className={`inline-flex items-center justify-center h-6 text-xs font-black px-4 rounded transition cursor-pointer ${
                              selectedVariantIndex === idx
                                ? "bg-[#0b6330] text-white"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                            }`}
                          >
                            {v.variant}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      onClick={() => setIsEnquiryModalOpen(true)}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#0f9648] hover:bg-[#0b6330] text-white font-bold py-3.5 px-6 rounded-lg shadow-sm hover:shadow transition text-sm sm:text-base cursor-pointer"
                    >
                      <CheckCircle2 className="w-5 h-5 shrink-0" /> Enquire Now
                    </button>

                    {product.vendorDetails?.phoneNumber && (
                      <a
                        href={`tel:${product.vendorDetails.phoneNumber}`}
                        className="flex-1 flex items-center justify-center gap-2 bg-[#1976d2] hover:bg-[#0d47a1] text-white font-bold py-3.5 px-6 rounded-lg shadow-sm hover:shadow transition text-sm sm:text-base text-center cursor-pointer"
                      >
                        <Phone className="w-5 h-5 shrink-0" /> Call Now
                      </a>
                    )}
                  </div>

                  {/* View Vendor Profile Button - only if vendor is not hidden */}
                  {product.vendorDetails?.vendorId && !product.vendorDetails?.hideFromStore && (
                    <Link
                      href={`/vendor-profile/${product.vendorDetails.slug || product.vendorDetails.vendorId}`}
                      className="flex items-center justify-center gap-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-bold py-3 px-6 rounded-lg transition text-sm cursor-pointer"
                    >
                      <Building2 className="w-4.5 h-4.5 shrink-0" />
                      View Vendor Profile
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              ) : (
                /* Locked Block for Anonymous Visitors */
                <div className="space-y-4">
                  <p className="text-xs text-gray-500 font-bold">
                    Subcategory: <span className="text-gray-700 font-black">{product.subcategory?.name}</span> |
                    Type: <span className="text-gray-700 font-black">{product.category?.name}</span>
                  </p>

                  <div className="bg-white text-gray-900 rounded-sm p-6 border border-gray-400 space-y-4">
                    <div className="flex items-start gap-3 relative z-10">
                      <Lock className="w-8 h-8 text-gray-500 shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-extrabold text-sm sm:text-base tracking-tight text-black flex items-center gap-1.5">
                          Buyer Registration Required
                        </h4>
                        <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                          Specifications, exact options, vendor phone contact numbers, and direct buy request
                          sheets are private. Register a free account to unlock access.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 pt-2 relative z-10">
                      <Link
                        href="/auth/login"
                        className="w-full text-center bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 rounded-lg text-sm shadow-xs transition"
                      >
                        Login to Unlock
                      </Link>
                      <Link
                        href="/auth/register"
                        className="w-full text-center bg-transparent border border-gray-700 hover:border-gray-500 hover:bg-gray-800/40 text-gray-900 font-bold py-3 rounded-lg text-sm transition"
                      >
                        Register as Buyer
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Description Text */}
            <div className="pt-4 border-t border-gray-100 space-y-1.5">
              <h3 className="font-bold text-gray-900 text-sm sm:text-base">Product Description</h3>
              <p className="text-gray-800 text-justify leading-relaxed text-sm sm:text-base whitespace-pre-line font-normal">
                {product.description || "No description provided for this product."}
              </p>
            </div>
          </div>
        </div>

        {/* Lower Sections: Specifications & Vendor Details */}
        <div className="space-y-10 relative mt-15">
          {/* Specifications Section */}
          {product.specification &&
            product.specification.length > 0 &&
            product.specification.map((spec, sIdx) => (
              <div key={sIdx} className="space-y-4">
                <div className="relative pb-2.5 mb-6">
                  <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">{spec.heading}</h2>
                  <div className="absolute bottom-0 left-0 w-14 h-[3px] bg-[#0f9648] rounded-full" />
                </div>

                <div className="w-full border-t border-b border-gray-200 divide-y divide-gray-200">
                  {spec.rows.map((row, rIdx) => (
                    <div key={rIdx} className="flex hover:bg-gray-55/40 transition-colors">
                      <div className="w-1/3 py-3.5 pr-6 font-bold text-gray-800 border-r border-gray-200 align-top text-xs sm:text-sm">
                        {row.key}
                      </div>
                      <div className="w-2/3 py-3.5 pl-6 text-gray-900 font-semibold text-xs sm:text-sm whitespace-pre-line">
                        {row.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Enquiry Form Modal */}
      {isEnquiryModalOpen && user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-auto overflow-hidden border border-gray-200 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-[#005a2b] p-5 text-white flex justify-between items-center border-b border-white/10">
              <div>
                <h3 className="font-black text-lg tracking-tight">Request Info & Quote</h3>
                <p className="text-xs text-gray-250 mt-1">
                  Submit your details to receive contact directly from the seller.
                </p>
              </div>
              <button
                onClick={() => setIsEnquiryModalOpen(false)}
                className="text-white/80 hover:text-white transition cursor-pointer p-1.5 rounded-full hover:bg-[#00401e]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[85vh] overflow-y-auto">
              {submitSuccess ? (
                <div className="text-center py-8 px-4 space-y-4">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto border border-green-200">
                    <CheckCircle2 className="w-10 h-10 text-[#0f9648]" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Enquiry Submitted!</h4>
                  <p className="text-sm text-gray-800 max-w-sm mx-auto">
                    Thank you for your interest. The vendor,{" "}
                    <strong className="text-gray-900">{product.vendorDetails?.name}</strong>, has been notified
                    of your enquiry and will contact you shortly.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitSuccess(false);
                      setIsEnquiryModalOpen(false);
                    }}
                    className="mt-4 bg-[#0f9648] hover:bg-[#0b6330] text-white font-bold px-6 py-2.5 rounded-lg text-sm transition shadow-sm cursor-pointer"
                  >
                    Close Window
                  </button>
                </div>
              ) : (
                <form onSubmit={handleEnquirySubmit} className="space-y-4">
                  {submitError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded font-semibold">
                      {submitError}
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 w-4.5 h-4.5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={buyerName}
                        onChange={(e) => setBuyerName(e.target.value)}
                        placeholder="Ravi Kumar"
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#0f9648] focus:ring-1 focus:ring-[#0f9648] rounded-lg text-sm transition-all text-gray-900 font-semibold"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 w-4.5 h-4.5 text-gray-400" />
                      <input
                        type="tel"
                        required
                        value={buyerPhone}
                        onChange={(e) => setBuyerPhone(e.target.value)}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#0f9648] focus:ring-1 focus:ring-[#0f9648] rounded-lg text-sm transition-all text-gray-900 font-semibold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                        Email (Optional)
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 w-4.5 h-4.5 text-gray-400" />
                        <input
                          type="email"
                          value={buyerEmail}
                          onChange={(e) => setBuyerEmail(e.target.value)}
                          placeholder="ravi@gmail.com"
                          className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#0f9648] focus:ring-1 focus:ring-[#0f9648] rounded-lg text-sm transition-all text-gray-900 font-semibold"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                        Location (Optional)
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 w-4.5 h-4.5 text-gray-400" />
                        <input
                          type="text"
                          value={buyerLocation}
                          onChange={(e) => setBuyerLocation(e.target.value)}
                          placeholder="City, State"
                          className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#0f9648] focus:ring-1 focus:ring-[#0f9648] rounded-lg text-sm transition-all text-gray-900 font-semibold"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Write details of your requirements here..."
                      className="w-full p-3 bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#0f9648] focus:ring-1 focus:ring-[#0f9648] rounded-lg text-sm transition-all text-gray-900 font-semibold"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setIsEnquiryModalOpen(false)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 px-4 rounded-lg text-sm transition text-center cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#0f9648] hover:bg-[#0b6330] text-white font-bold py-3 px-4 rounded-lg shadow-sm transition disabled:opacity-75 disabled:cursor-not-allowed text-sm cursor-pointer"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin shrink-0" /> Submitting...
                        </>
                      ) : (
                        "Submit Enquiry"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
